/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Button, Modal } from 'semantic-ui-react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { selectCurrentUserApiToken, selectCurrentUserId, selectCurrentUserName, selectCurrentUserNotificationEmail, selectCurrentUserNumberUnclearedNotifications, selectCurrentUserNumberUnreadNotifications, numberUnClearedNotificationsChanged, numberUnreadNotificationsChanged, userLoggedOut, selectDisplayTheTermsOfAgreement, changeSseConnectionId } from "../slices/currentUser/currentUserSlice";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { selectEnvironment } from "../slices/environment/environmentSlice";
import { resourceUpdated } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectToolName } from "../slices/footer/footerSlice";
import { selectFoiFocus } from "../slices/foiOfFocus/foiOfFocusSlice";
import { getUserNotificationNumbers } from "../slices/notifications/notificationsFunctionsAPI";
import getFhirJson from "../functions/getFhirJsonAPI";
import NotificationsView from "./NotificationsView";
import { ConfirmModal } from "../components/ConfirmModal";
import server_config from '../ServerConfiguration';
import { ReactComponent as BellEmoji } from '../assets/bellemoji.svg';
import "../App.css";

const fevir_node_server_address = server_config["fevir_node_server_address"];
//const fevir_website_address = server_config["fevir_website_address"];

const onLogoutClicked = async (dispatch) => {
    await firebase.auth().signOut().then(async () => {
        console.log("Signing out...");
        dispatch(userLoggedOut());
    }).catch((error) => { console.log(error); });
}

const updateResourceInCollection = async (foi, apiToken, dispatch) => {
    //console.log("referenced " + foi + " changed");
    let fhirJson = await getFhirJson(foi, null, apiToken);
    dispatch(resourceUpdated({ "id": foi, "resourceJson": fhirJson, "revisedResourceJson": fhirJson, "sourceOfChanges": "from-server" }));
}

const useOnClickOutside = (ref, handler) => {
    useEffect(() => {
        const listener = (event) => {
            // Do nothing if clicking ref's element or descendent elements
            if (!ref.current || ref.current.contains(event.target)) {
                return;
            }
            handler(event);
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler]);
}

const ResourceRedirectToast = ({ foi, message }) => {
    //Makes the toast clickable to get to the resource in a new tab
    if (foi) {
        return <div style={{ cursor: "pointer", width: "720px !important" }} 
        onClick={() => { window.open("/" + foi, '_blank').focus(); }}>
            <a target="_blank">{message}</a>
        </div>
    } else {
        return <div><span>{message}</span></div>
    }
}

const NotificationModal = ({ notificationHover, notificationLeave,
    notificationModalOpen, setNotificationModalOpen }) => {
    const modalRef = useRef();

    const modalClose = () => {
        setNotificationModalOpen(false);
    }

    useOnClickOutside(modalRef, () => modalClose());

    return <Modal
        className="notificationsModal"
        style={{ right: "10px", top: "30px", width: "900px", maxWidth: "70%", margin: "16px" }}
        onMouseEnter={notificationHover}
        onMouseLeave={notificationLeave}
        open={notificationModalOpen}
        dimmer={false}
        centered={false}
        content={<div ref={modalRef} style={{ whiteSpace: "pre-wrap" }}>
            <div className="notificationsModalDiv scrolling content" style={{ padding: "10px 16px 10px 16px" }}>
                <NotificationsView />
            </div>
        </div>}
        closeOnEscape
        closeOnDimmerClick={false}
        onClose={modalClose}
    />
}

const reloadPage = () => { //Because for some reason passing window.location.reload didn't work
    window.location.reload();
}

const Navbar = ({ userLoadingState, setFirebaseModalState }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const path = location.pathname.substring(1);
    const apiToken = useSelector(selectCurrentUserApiToken);
    const foi = useSelector(selectFoiFocus);
    const foiRef = useRef(foi);
    const editMode = useSelector(selectEditMode);
    const editModeRef = useRef(editMode);
    const userId = useSelector(selectCurrentUserId);
    const userName = useSelector(selectCurrentUserName);
    const isLoggedIn = (!!userId && userId !== "1");
    const toolName = useSelector(selectToolName);
    const environment = useSelector(selectEnvironment);
    const notificationemail = useSelector(selectCurrentUserNotificationEmail);
    const numberUnreadNotifications = useSelector(selectCurrentUserNumberUnreadNotifications);
    const numberUnclearedNotifications = useSelector(selectCurrentUserNumberUnclearedNotifications);
    //const { notificationBellRef } = useRef();
    const hoverNotificationBellRef = useRef(null);
    const hoverLeaveNotificationBellRef = useRef(null);
    const { addToast } = useToasts();
    const navigate = useNavigate();
    const displayTheTermsOfAgreement = useSelector(selectDisplayTheTermsOfAgreement);

    let subpageClass = "";
    if (path) {
        subpageClass = " navbarSubPage";
    }
    let onFevir = (environment === "FEvIR" || environment === "Development FEvIR");
    let fevirNavClass = "";
    if (onFevir) {
        fevirNavClass = " fevirNavbar";
    }

    const [bellShaking, setBellShaking] = useState(false);
    const [newNotificationBoolean, setNewNotificationBoolean] = useState(false); //TODO -- determine how to change it
    const [notificationModalOpen, setNotificationModalOpen] = useState(false);
    const [confirmModalState, setConfirmModalState] = useState({ open: false });

    let isUnloading = false;
    window.addEventListener('beforeunload', () => {
        isUnloading = true;
    });

    useEffect(() => {
        if (displayTheTermsOfAgreement === true) {
            navigate("/termsofagreement");
        }
    }, [displayTheTermsOfAgreement]);

    useEffect(() => {
        foiRef.current = foi;
    }, [foi]);

    useEffect(() => {
        editModeRef.current = editMode;
    }, [editMode]);

    useEffect(() => {
        if (apiToken) { //And should do one more thing to ensure that it wasn't already established, probably another state
            const sseUrl = `${fevir_node_server_address}sse?apiToken=${encodeURIComponent(apiToken)}`;
            const eventSource = new EventSource(sseUrl, {}); //withCredentials: true
            eventSource.onmessage = async (event) => {
                const data = JSON.parse(event.data);
                if (data.connectionId) {
                    dispatch(changeSseConnectionId(data.connectionId));
                }
                if (data?.action === "update-notificationnumber-states-and-toast") {
                    if (data.message) {
                        addToast(<ResourceRedirectToast foi={data.target} message={data.message.substring(0, 500)} />, { appearance: 'info' });
                    }
                    setNewNotificationBoolean(true);

                    const numbersResponse = await getUserNotificationNumbers(apiToken);
                    if (numbersResponse.success) {
                        //let newUnread = (numberUnreadNotifications !== numbersResponse.numberunreadnotifications);
                        let newUncleared = (numberUnclearedNotifications !== numbersResponse.numberunclearednotifications);
                        dispatch(numberUnreadNotificationsChanged(numbersResponse.numberunreadnotifications));
                        //if (newUnread) { }
                        if (newUncleared) {
                            dispatch(numberUnClearedNotificationsChanged(numbersResponse.numberunclearednotifications));
                        }
                    }
                } else if (data?.action === "resource-updated-in-another-session") {
                    if (data.foi == foiRef.current) { //This HAS to be a double equals and not triple because one is often a string vs a number
                        let message;
                        if (editModeRef.current === true) {
                            message = "Do you want to refresh this page so you'll be seeing the latest version of it, you'll lose your work? You can say 'No' and save your work and compare the version differences.";
                        } else {
                            message = "Do you want to refresh this page so you'll be seeing the latest version of it?";
                        }
                        setConfirmModalState({
                            open: true, header: "This resource has been updated",
                            content: message,
                            runFunction: reloadPage, "functionParameters": null,
                            saveFunction: null, buttonTextOkay: "Yes", buttonTextCancel: "No",
                            buttonColorOkay: "green", buttonColorCancel: "red"
                        });
                    } else { //Means it's a resource that's referenced that was updated
                        //(or a referenced resource)
                        if (!editModeRef.current || data.userid === userId) {
                            updateResourceInCollection(data.foi, apiToken, dispatch);
                        } else {
                            //TODO for future use -- confirm modal if yes updateResourceInCollection
                            console.log(data.foi);
                        }
                    }
                } else if (data?.action === "hello-world") {
                    //Placeholder action
                }
            };

            eventSource.onerror = (error) => {
                if (!isUnloading) {     //If there's an error with this sse event AND it's not because the tab is closing, then console log it
                    console.error('EventSource failed:', error);
                }
                eventSource.close();
            };

            return () => {
                eventSource.close();
            };

        }
    }, [apiToken]);

    useEffect(() => {
        if (newNotificationBoolean === true) {
            setBellShaking(true);
            const timer = setTimeout(() => {
                // Sets the bell to have a .shake class and changes it after it's done
                setBellShaking(false);
                if (newNotificationBoolean) { //Checks to make sure nothing else set it to false
                    setNewNotificationBoolean(false);
                }
            }, 500); // Matches the animation duration set in the CSS
            return () => clearTimeout(timer);
        }
    }, [newNotificationBoolean]);

    const notificationHover = () => {
        clearTimeout(hoverLeaveNotificationBellRef.current);
        hoverNotificationBellRef.current = setTimeout(() => { setNotificationModalOpen(true); }, 400);
    }

    const notificationLeave = () => {
        clearTimeout(hoverNotificationBellRef.current);
        hoverLeaveNotificationBellRef.current = setTimeout(() => { setNotificationModalOpen(false); }, 600);
    }

    return <div id="navbar" name="navbar" className={"navbar " + path + subpageClass + fevirNavClass}
        style={{ margin: "0px", width: "100%", position: "fixed" }}>
        <NotificationModal notificationHover={notificationHover} notificationLeave={notificationLeave}
            notificationModalOpen={notificationModalOpen} setNotificationModalOpen={setNotificationModalOpen} />
        {confirmModalState.open &&
            <ConfirmModal confirmModalState={confirmModalState} setConfirmModalState={setConfirmModalState} />}
        <table className="headerTable">
            <tbody>
                <tr>
                    <td className="navbarWebsiteLinkCell" style={{ whiteSpace: "nowrap", paddingLeft: ".2rem" }}>
                        {
                            environment === "Development FEvIR"
                                ?
                                <h2>
                                    <a href="/" className={"unselectable"}>
                                        <span style={{ color: "red" }}>Development FEvIR</span>
                                    </a>
                                </h2>
                                :
                                environment === "FEvIR"
                                    ?
                                    <h2>
                                        <a href="/" className={"unselectable"}>
                                            <span>FEvIR Platform</span>
                                        </a>
                                    </h2>
                                    :
                                    <>
                                        <Link to="" className="navbar-logo-container">
                                            <img id="navbarlogo" className="navbar-logo navbar-logo-wide"
                                                alt="Computable Publishing logo" src="/png-01-wide-tiny.png" />
                                            <img className="navbar-logo navbar-logo-small"
                                                alt="Computable Publishing logo" src="/cplogo-192.png" />
                                        </Link>
                                    </>
                        }
                    </td>
                    <td style={{ width: "100%", textAlign: "center" }}>
                        {
                            (!toolName || toolName === "FEvIR® Platform")
                            &&
                            <div id="navbarPageLinks" className="right-justify navbar-page-links">
                                {
                                    environment === "Computable Publishing" &&
                                    <Link to={window.location.href.includes("computablepublishing.com") ? "https://fevir.net" : "/"}>FEvIR</Link>
                                }
                                <Link to="/mission" className={path === "mission" ? "selectedNavbarSubPage" : ""}>Making Science Computable</Link>
                                <Link to="/products"><span className={path === "products" ? "selectedNavbarSubPage" : ""}>Products</span></Link>
                                <Link to="/media" className={path === "media" ? "selectedNavbarSubPage" : ""}>Presentations</Link>
                                <Link to="/about" className={path === "about" ? "selectedNavbarSubPage" : ""}>Team</Link>
                            </div>}
                    </td>
                    <td className="navbarUserInfoCell" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                        <div className="right-justify" style={{ margin: "6px 1px 0px 0px" }}>
                            <span style={{ paddingLeft: "20px" }}></span>
                            {isLoggedIn ?
                                <>
                                    {numberUnclearedNotifications > 0 &&
                                        <span>
                                            <span className={"unselectable " + (bellShaking ? "shake" : "noShake")}
                                                style={{
                                                    display: "inline-block", textAlign: "left",
                                                    width: "60px", marginRight: "-36px"
                                                }}>
                                                <span
                                                    className={"navigationNotificationBell" + (numberUnreadNotifications ? "" : " bellNoUnread")}>
                                                    <Link to={`/content/myresources`}
                                                        style={{ cursor: "pointer" }}
                                                        onMouseEnter={notificationHover}
                                                        onMouseLeave={notificationLeave}
                                                        onClick={notificationLeave}
                                                    >
                                                        <BellEmoji className="inline-emoji" />
                                                    </Link>
                                                </span>
                                                {numberUnreadNotifications > 0 &&
                                                    <span style={{
                                                        pointerEvents: "none", display: "inline-block",
                                                        position: "relative", textAlign: "center", overflow: "visible",
                                                        top: "-2px", left: "-43.50px", width: "60px"
                                                    }}>
                                                        <span style={{ zIndex: "3000", color: "#000000" }}>
                                                            {numberUnreadNotifications < 100 ?
                                                                numberUnreadNotifications
                                                                :
                                                                "99+"}
                                                        </span>
                                                        {/* TODO - the number unread is not displaying because it is behind the bell */}
                                                        {/* Currently 99+ doesn't look good, worse than 100 */}
                                                    </span>
                                                }
                                            </span>
                                        </span>
                                    }
                                    <span style={{ paddingLeft: "10px" }}></span>
                                    <Link to={'/editprofile'}>
                                        <span style={{ fontSize: onFevir ? '14px' : '18px', textDecoration: "underline" }}>
                                            {userName || "Edit User Profile"}
                                        </span>
                                    </Link>
                                    {(!notificationemail || notificationemail === "") &&
                                        <span style={{ paddingLeft: "4px" }}>
                                            <img style={{ height: '16px' }} src="/red-excl.png"
                                                alt="Incomplete profile, please complete"
                                                title="Incomplete profile, please complete" />
                                        </span>
                                    }
                                    <span style={{ paddingLeft: "6px" }}>
                                        <Button className="formButton logOutLogInButton"
                                            style={{ marginLeft: "4px", width: "83px" }}
                                            content="Log Out" compact
                                            onClick={() => { onLogoutClicked(dispatch) }} />
                                    </span>
                                </>
                                :
                                <>
                                    <span>
                                        <Button disabled={userLoadingState}
                                            className="formButton logOutLogInButton"
                                            style={{ marginLeft: "4px" }}
                                            content="Sign In / Create Account" compact
                                            onClick={() => {
                                                /*
                                                //This is for if we bring back Single Sign-On on computablepublishing.com
                                                if (window.location.href.includes("computablepublishing.com")) {
                                                    let fevirSsoAddress = fevir_website_address + "/noheader/sso?redirect=" + encodeURIComponent(window.location.origin + window.location.pathname);
                                                    window.location.assign(fevirSsoAddress);
                                                } else {
                                                    setFirebaseModalState(prevState => { return { ...prevState, 'open': true } }); //Regular fevir.net sign in where it opens up the modal
                                                }
                                                */
                                                setFirebaseModalState(prevState => { return { ...prevState, 'open': true } }); //Regular fevir.net sign in where it opens up the modal
                                            }} />
                                    </span>
                                </>}
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>;
}

export default Navbar;